/**
 * Google CSA Iframe Click Tracking
 *
 * Sets up heuristic click tracking around CSA iframes. Because the iframes are
 * cross-origin, we detect interactions via unload and postMessage events.
 * Ported verbatim from @serp/client; used by the CSA (`_googCsa("ads")`)
 * ad-type handler.
 */

import {
  appendPartnerCookies,
  sendPingback as sendBeacon,
} from "@ascent/papi-core/client";

/**
 * CSA renders inside Google-hosted iframes. We track these origins to infer
 * click behaviour because we cannot bind directly to the iframe DOM.
 */
const GOOGLE_ORIGINS = [
  "https://www.adsensecustomsearchads.com",
  "https://www.googleadservices.com",
  "https://syndicatedsearch.goog",
];

let iframeTrackingInitialized = false;

/**
 * Sets up heuristic click tracking around CSA iframes.
 * A module-level guard prevents duplicate listeners when called multiple times.
 */
export function setupGoogleIframeClickTracking(): void {
  if (typeof window === "undefined" || iframeTrackingInitialized) {
    return;
  }

  iframeTrackingInitialized = true;

  window.addEventListener("beforeunload", handleBeforeUnload);
  window.addEventListener("message", handleMessage);
}

/**
 * Resets the tracking state (useful for testing).
 */
export function resetIframeTracking(): void {
  if (typeof window === "undefined") {
    return;
  }

  if (iframeTrackingInitialized) {
    window.removeEventListener("beforeunload", handleBeforeUnload);
    window.removeEventListener("message", handleMessage);
    iframeTrackingInitialized = false;
  }
}

/**
 * Checks if iframe tracking has been initialized.
 */
export function isIframeTrackingInitialized(): boolean {
  return iframeTrackingInitialized;
}

function handleBeforeUnload(): void {
  const activeElement = document.activeElement;

  if (activeElement?.tagName === "IFRAME") {
    const src = activeElement.getAttribute("src");

    if (!src) {
      return;
    }

    try {
      const source = new URL(src);
      if (GOOGLE_ORIGINS.includes(source.origin)) {
        const clickTrackUrl = getClickTrackUrl(
          activeElement.parentElement as HTMLElement,
        );
        sendPingback(clickTrackUrl);
      }
    } catch {
      // Ignore invalid iframe src URLs
    }
  }
}

function handleMessage(event: MessageEvent): void {
  if (!GOOGLE_ORIGINS.includes(event.origin)) {
    return;
  }

  const activeElement = document.activeElement;
  if (
    document.visibilityState === "hidden" &&
    activeElement?.tagName === "IFRAME"
  ) {
    const clickTrackUrl = getClickTrackUrl(
      activeElement.parentElement as HTMLElement,
    );
    sendPingback(clickTrackUrl);
  }
}

function getClickTrackUrl(parentDiv: HTMLElement | null): URL | null {
  if (!parentDiv) {
    return null;
  }

  const beaconUrl =
    parentDiv.getAttribute("data-ad-clicked-beacon-url") ?? null;

  if (!beaconUrl) {
    return null;
  }

  try {
    return new URL(beaconUrl);
  } catch {
    return null;
  }
}

function sendPingback(pingbackUrl: URL | null): void {
  if (!pingbackUrl) {
    return;
  }

  appendPartnerCookies(pingbackUrl);
  sendBeacon(pingbackUrl);
}
